/* eslint-disable react/no-danger */

import React, { useState, useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import FontAwesome from 'react-fontawesome';
import { DragSource, DropTarget } from 'react-dnd';
import { useDispatch, useSelector, connect } from 'react-redux';
import moment from '~/Providers/Moment';
import mParticle, { CompleteTaskEvent } from '~/Providers/mParticle';
import { DAY_CHECKLIST } from '~/Config/DragTypes';
import { DATE_FORMAT, DATE_TIME_FORMAT } from '~/Redux/Constants';
import QuillInput from '~/Components/Quill/QuillInput';
import ClockOpen from '~/Components/Global/Assets/Svgs/ClockOpen';
import { toStartOfWeekForChecklist } from '~/Context/Checklist';
import { showSnooze } from '~/Redux/Modules/SnoozePopup';
import { MONTH_TODO, WEEK_TODO } from '~/Config/Checklists';
import {
  createTask,
  updateTask,
  deleteTask,
  restoreTask,
  showRepeatRulePopup,
} from '~/Redux/Modules/Tasks';
import { addUndo } from '~/Redux/Modules/Undo';
import Store from '~/Redux/Store';
import { showConfirmWithSettings } from '~/Redux/Modules/EventConfirmation';
import { guid } from '~/util';
import { UPDATE_ITEM } from '~/Redux/Modules/Tasks/constants';
import { nextPriority } from './helpers';
import TaskCheckbox from './TaskCheckbox';

import './Tasks.scss';

const taskDrag = {
  beginDrag: ({ task }) => ({
    task,
  }),
};

const taskTarget = {
  canDrop: ({ task: { id } }, monitor) => {
    return id !== monitor.getItem().task.id;
  },
  drop: (target, monitor) => {
    const task = monitor.getItem().task;
    const next = target.task;
    const prev = target.prevTask;
    const updatedTask = {
      ...task,
      start: next.start,
      type: next.type,
      priority: nextPriority(prev?.priority || 'aaaaa', next.priority),
      repeat_rule: null,
      confirm: 'this',
    };
    if (task.type !== next.type) {
      Store.dispatch(
        deleteTask({ ...task, recurrence_id: task.recurrence_id || task.start, confirm: 'this' })
      );
      Store.dispatch(createTask(updatedTask));
    } else {
      Store.dispatch(
        updateTask({ ...updatedTask, recurrence_id: task.recurrence_id || task.start })
      );
    }
  },
};

const dragCollect = (connect, monitor) => ({
  connectDragPreview: connect.dragPreview(),
  connectDragSource: connect.dragSource(),
  isDragging: monitor.isDragging(),
});

const dropCollect = (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver(),
  canDrop: monitor.canDrop(),
});

const useThrottledUpdate = (task, summary, hasFocus) => {
  const interval = useRef();
  const lastId = useRef(task?.id);
  const lastSummary = useRef(summary);
  const dispatch = useDispatch();

  useEffect(() => {
    if (task?.id !== lastId.current && !lastId?.current?.startsWith('temp-')) {
      lastSummary.current = summary;
    }
    lastId.current = task?.id;

    if (lastSummary.current !== summary && lastId.current && !lastId.current.startsWith('temp-')) {
      if (interval.current) clearTimeout(interval.current);
      lastSummary.current = summary;
      if (!hasFocus) return;
      interval.current = setTimeout(() => {
        console.log(`update task ${task.id} with summary ${summary}`);
        dispatch(updateTask({ ...task, summary }));
      }, 300);
    }
  }, [interval, lastId, lastSummary, dispatch, task, summary, hasFocus]);
  return () => {
    if (interval.current) clearTimeout(interval.current);
    interval.current = null;
  };
};

const getPaddingForFont = fontName => {
  switch (fontName) {
    case 'Give You Glory':
    case 'Shadows Into Light':
    case 'Liu Jian Mao Cao':
    case 'Handlee':
      return 4;
    case 'Architects Daughter':
    case 'Poppins':
    case 'Crafty Girls':
    case 'Bellota':
      return 3.5;
    case 'Swanky and Moo Moo':
      return 3;
    case 'Raleway':
      return 2.5;
    default:
      return 4;
  }
};

export const TaskItem = ({
  task,
  lastTask,
  isDragging,
  addSizeRef,
  connectDropTarget,
  connectDragPreview,
  connectDragSource,
  isOver,
  canDrop,
  expand,
  nextTask,
  disableTaskWrapping,
  font,
}) => {
  const dispatch = useDispatch();
  const isCompleted = !!task.completed_at;
  const lastId = useRef(task?.id);
  const lastUpdate = useRef(task?.summary || '');
  const [summary, setSummary] = useState(task.summary);
  const [hasFocus, setHasFocus] = useState(false);
  const cancelThrottledUpdate = useThrottledUpdate(task, summary, hasFocus);
  const userOptions = useSelector(({ UserOptions }) => UserOptions.data);
  const moveCompletedTasksToBottom = userOptions?.move_completed_tasks_to_bottom ?? true;
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    if (!hasFocus || (task?.id !== lastId.current && !lastId?.current?.startsWith('temp-'))) {
      lastUpdate.current = task?.summary || '';
      setTimeout(() => {
        setSummary(task.summary);
      });
    }
    lastId.current = task?.id;
  }, [task, lastUpdate, lastId, hasFocus]);

  const createNewItem = (summary, lastPriority) => {
    if (!summary) return;
    const newTask = {
      type: task.type,
      start: task.start,
      summary,
      priority: nextPriority(lastPriority || task.priority, nextTask?.priority),
    };
    dispatch(createTask(newTask));
    return newTask.priority;
  };

  const handleOnChange = ({ summary }) => {
    expand?.();

    const items = summary.split('\n');
    if (items.length > 1) {
      summary = items[0];
      items.slice(1, items.length).reduce((a, item) => createNewItem(item, a), null);
      dispatch(updateTask({ ...task, summary }));
      cancelThrottledUpdate();
    }

    lastUpdate.current = summary;
    setSummary(summary);
  };

  const handleOnEnter = suffix => {
    dispatch(updateTask({ ...task, summary }));
    cancelThrottledUpdate();
    const tempId = `temp-${guid()}`;
    dispatch(
      createTask({
        tempId,
        start: task.start,
        type: task.type,
        summary: suffix,
        priority: nextPriority(task.priority, nextTask?.priority),
      })
    );
    setTimeout(() => {
      const element =
        document.querySelector(`#task-${task.type}-${task.startString}-${tempId} .ql-editor`) ||
        document.querySelector(`#create-${task.type}-${task.startString} .ql-editor`);
      element?.focus();
    });
  };

  const handleOnBlur = () => {
    setHasFocus(false);
    dispatch(updateTask({ ...task, summary }));
    cancelThrottledUpdate();
  };

  const handleOnFocus = () => {
    setHasFocus(true);
  };

  const handleShowSnooze = e => {
    setDisabled(true);
    console.log('handleShowSnooze', e, e.target, e.target.getBoundingClientRect());
    const { bottom, left, width } = e.target.getBoundingClientRect();

    dispatch(
      showSnooze(task.startDate, { top: bottom, left: left + width / 2 }, snoozeToDay => {
        console.log('snooze to', snoozeToDay.format(DATE_FORMAT));
        if (task.type == MONTH_TODO) snoozeToDay.startOf('month');
        if (task.type == WEEK_TODO) snoozeToDay = toStartOfWeekForChecklist(snoozeToDay);
        if (snoozeToDay.isSame(task.startDate, 'day')) {
          console.log('snoozing to the same place - noop');
          return;
        }
        const currentStart = task.start;
        dispatch(
          updateTask({
            ...task,
            start: snoozeToDay.format(DATE_FORMAT),
            recurrence_id: task.recurrence_id || task.start,
            confirm: 'this',
          })
        );

        dispatch(
          addUndo({
            title: 'Task snoozed',
            callback: () => {
              dispatch(
                updateTask({
                  ...task,
                  start: currentStart,
                  recurrence_id: task.recurrence_id || task.start,
                  confirm: 'this',
                  unsnooze: true,
                })
              );
            },
          })
        );
        setDisabled(false);
      })
    );
  };

  const onConfirmed = confirm => {
    const setRecurrence = ['this_event', 'following_events'].find(item => item === confirm);
    const recurrence_id = setRecurrence ? task.recurrence_id || task.start : task.recurrence_id;
    dispatch(deleteTask({ ...task, confirm, recurrence_id })).then(() => {
      dispatch(
        addUndo({
          title: 'Task deleted',
          callback: () => {
            dispatch(restoreTask({ ...task, confirm, recurrence_id }));
          },
        })
      );
    });
  };

  const removeItem = () => {
    if (task.repeat_rule) {
      dispatch(
        showConfirmWithSettings({
          heading: 'Delete recurring task',
          onConfirm: onConfirmed,
          model: 'Task',
        })
      );
    } else {
      onConfirmed('this_event');
    }
  };

  const toggle = async () => {
    let { priority, completed_at } = task;
    if (isCompleted) {
      completed_at = null;
    } else {
      completed_at = moment().format(DATE_TIME_FORMAT);
      mParticle.logEvent(new CompleteTaskEvent());
      if (moveCompletedTasksToBottom) {
        priority = nextPriority(lastTask.priority);
      }
    }

    dispatch({
      type: 'artful-client/Task/UPDATE_ITEM',
      data: {
        ...task,
        completed_at,
      },
    });

    if (moveCompletedTasksToBottom && !isCompleted) {
      await new Promise(r => setTimeout(r, 50));
      dispatch({
        type: 'artful-client/Task/UPDATE_ITEM',
        data: {
          ...task,
          completed_at,
          priority,
        },
      });
    }

    dispatch(
      updateTask({
        ...task,
        priority,
        completed_at,
        confirm: 'this',
      })
    );
  };

  const handleShowRepeat = e => {
    const { bottom, left, width } = e.target.getBoundingClientRect();
    dispatch(
      showRepeatRulePopup(
        task,
        { top: bottom, left: left + width / 2 },
        (repeat_rule, confirm, auto_rollover) => {
          console.log(auto_rollover);
          dispatch(
            updateTask({
              ...task,
              repeat_rule,
              confirm,
              recurrence_id: task.recurrence_id || task.start,
              auto_rollover,
              rolloverChanged: auto_rollover != task.auto_rollover,
            })
          );
        }
      )
    );
  };

  const paddingTop = getPaddingForFont(font ? font.css : 'Raleway');

  return connectDropTarget(
    connectDragPreview(
      <div
        className={`task-item ${isCompleted ? 'complete' : ''} ${
          isOver && canDrop ? 'is-over' : ''
        } ${isDragging ? 'is-dragging' : ''}`}
      >
        <div className={`flex-row ${disableTaskWrapping ? 'disable-task-wrapping' : ''}`}>
          <span
            style={{
              visibility: 'hidden',
              position: 'absolute',
              width: 'unset',
              whiteSpace: 'nowrap',
              padding: 0,
              height: '2.6rem',
            }}
            ref={addSizeRef}
          >
            {task.summary}
          </span>
          {connectDragSource(
            <div className="task-drag-handle">
              <TaskCheckbox
                disabled={disabled}
                onClick={toggle}
                completed={isCompleted}
                paddingTop={paddingTop}
              />
            </div>
          )}
          <QuillInput
            id={`task-${task.type}-${task.startString}-${task.id}`}
            name="summary"
            value={summary}
            onChange={handleOnChange}
            onEnter={handleOnEnter}
            onBlur={handleOnBlur}
            onFocus={handleOnFocus}
            formGroup={false}
            disabled={disabled}
          />
          <span
            key="rp"
            className={`material-icons task-repeat ${task.repeat_rule ? 'repeats' : ''}`}
            onClick={e => disabled || handleShowRepeat(e)}
            style={{ paddingTop: `${paddingTop}px` }}
          >
            repeat
          </span>
          <span
            key="sn"
            className="task-snooze"
            onClick={e => disabled || handleShowSnooze(e)}
            style={{ paddingTop: `${paddingTop}px` }}
          >
            <ClockOpen />
          </span>
          <FontAwesome
            key="dl"
            className="task-delete"
            name="trash-o"
            onClick={() => disabled || removeItem()}
            style={{ paddingTop: `${paddingTop}px` }}
          />
        </div>
      </div>
    )
  );
};

TaskItem.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.string.isRequired,
    start: PropTypes.string.isRequired,
    summary: PropTypes.string,
    startDate: PropTypes.object.isRequired,
  }).isRequired,
  lastTask: PropTypes.shape({ priority: PropTypes.string.isRequired }),
  isDragging: PropTypes.bool,
  addSizeRef: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  isOver: PropTypes.bool,
  canDrop: PropTypes.bool,
  expand: PropTypes.func.isRequired,
  disableTaskWrapping: PropTypes.bool,
  font: PropTypes.object,
};

const mapStateToProps = state => ({
  font: state.UserOptions.data.font,
});

export default connect(mapStateToProps)(
  memo(
    TaskItem
      |> DropTarget(DAY_CHECKLIST, taskTarget, dropCollect)
      |> DragSource(DAY_CHECKLIST, taskDrag, dragCollect)
  )
);
